import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { onAuthStateChanged } from 'firebase/auth'
import { auth } from '@/services/firebase'
import {
  signIn as authSignIn,
  signUp as authSignUp,
  signOutUser,
  sendPasswordReset,
} from '@/services/auth.service'
import type { AuthContextValue, AuthUser, BaseUserRole, UserRole } from '@/types'
import { getEffectiveUserRole, normalizeInstituteRoles } from '@/utils/instituteRoles'

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

/**
 * Top-level auth context provider.
 *
 * Subscribes to Firebase `onAuthStateChanged`, extracts the base `role`
 * claim plus `instituteRoles` from the ID token, and exposes `signIn`,
 * `signUp`, `signOut`, `resetPassword`, and `refreshToken` to the
 * entire component tree via `useAuthContext()`.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  /** Build AuthUser from a Firebase User + token claims */
  const buildAuthUser = async (firebaseUser: import('firebase/auth').User): Promise<AuthUser> => {
    const tokenResult = await firebaseUser.getIdTokenResult()
    const instituteRoles = normalizeInstituteRoles(tokenResult.claims.instituteRoles)
    // Custom claims contain both 'role' (base auth role) and 'instituteRoles' (institute-scoped roles).
    const baseRole: BaseUserRole = tokenResult.claims.role === 'superuser' ? 'superuser' : 'user'
    const role: UserRole = getEffectiveUserRole(instituteRoles, baseRole)

    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      role,
      instituteRoles,
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(await buildAuthUser(firebaseUser))
      } else {
        setUser(null)
      }
      setLoading(false)
    })

    return unsubscribe
  }, [])

  const signIn = async (email: string, password: string) => {
    await authSignIn(email, password)
    // Auth state change observer will update user
  }

  const signUp = async (email: string, password: string, displayName: string) => {
    await authSignUp(email, password, displayName)
  }

  const signOut = async () => {
    await signOutUser()
    setUser(null)
  }

  const resetPassword = async (email: string) => {
    await sendPasswordReset(email)
  }

  /**
   * Force-refresh the ID token to pick up updated custom claims
   * (e.g. after institute membership mutation via syncInstitutesClaim).
   */
  const refreshToken = async () => {
    const firebaseUser = auth.currentUser
    if (firebaseUser) {
      await firebaseUser.getIdToken(true)
      setUser(await buildAuthUser(firebaseUser))
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut, resetPassword, refreshToken }}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Consume the auth context created by `AuthProvider`.
 *
 * Returns the current `AuthContextValue` (user, loading, and auth
 * action methods). Throws if called outside `<AuthProvider>`.
 */
export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}
